/**
 * Toast-style messages (see common/templates/common/partials/messages.html).
 * Server-rendered Django messages and ones shown via showMessage() share the
 * same markup and behaviour: dismiss on click, or fade out after a delay.
 */
import { getOpenDialogElement } from './modals.js'

const DISPLAY_DURATION_MS = 3000
const FADE_DURATION_MS = 500

// Matches Tailwind's md breakpoint, from which the stack is top-anchored.
const DESKTOP_QUERY = '(min-width: 48rem)'

const hideMessage = (message) => {
    clearTimeout(message.hideTimeout)
    message.classList.add('opacity-0')
    setTimeout(() => message.remove(), FADE_DURATION_MS)
}

const scheduleHide = (message) => {
    clearTimeout(message.hideTimeout)
    message.hideTimeout = setTimeout(
        () => hideMessage(message),
        DISPLAY_DURATION_MS
    )
}

const initMessage = (message) => {
    message.addEventListener('click', () => hideMessage(message))
    scheduleHide(message)
}

/**
 * Screen readers ignore everything outside an open aria-modal dialog,
 * including the toast stack, so while one is open the text is announced
 * through the dialog's own live region instead. Clearing it first means
 * repeating the same text is announced again.
 */
const announceInOpenDialog = (text) => {
    const status = getOpenDialogElement()?.querySelector('[data-dialog-status]')
    if (!status) return
    status.textContent = ''
    requestAnimationFrame(() => {
        status.textContent = text
    })
}

/**
 * Show a toast message, matching Django's messages framework styling.
 * Showing the same text again while it's still on screen restarts that
 * toast's timer instead of stacking a duplicate.
 */
export const showMessage = (text) => {
    const stack = document.getElementById('message-stack')
    const template = document.getElementById('message-template')
    if (!stack || !template) {
        console.warn(
            'showMessage: #message-stack or #message-template is missing ' +
                '(is common/partials/messages.html included?)',
            text
        )
        return
    }

    announceInOpenDialog(text)

    const existing = Array.from(
        stack.querySelectorAll('.message-container:not(.opacity-0)')
    ).find((message) => message.textContent.trim() === text)
    if (existing) {
        scheduleHide(existing)
        return
    }

    const message = template.content.firstElementChild.cloneNode(true)
    message.querySelector('li').textContent = text
    stack.appendChild(message)
    initMessage(message)
}

/**
 * Mobile browsers don't shrink the layout viewport when the on-screen
 * keyboard opens (iOS Safari, and Android Chrome since v108), so a
 * bottom-anchored fixed element ends up behind it. The visual viewport does
 * shrink, so the gap between the two is the keyboard's height. From the md
 * breakpoint up the stack is top-anchored, so updates are skipped there.
 */
const trackKeyboardOffset = (stack) => {
    const viewport = window.visualViewport
    if (!viewport) return

    const desktop = window.matchMedia(DESKTOP_QUERY)
    let lastOffset = null

    const update = () => {
        const offset = desktop.matches
            ? 0
            : Math.max(
                  0,
                  Math.round(
                      window.innerHeight - viewport.height - viewport.offsetTop
                  )
              )
        if (offset === lastOffset) return
        lastOffset = offset
        stack.style.setProperty('--keyboard-offset', `${offset}px`)
    }

    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    desktop.addEventListener('change', update)
    update()
}

document.addEventListener('DOMContentLoaded', () => {
    const stack = document.getElementById('message-stack')
    if (!stack) return

    stack.querySelectorAll('.message-container').forEach(initMessage)
    trackKeyboardOffset(stack)
})
